import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, EmptyState, RowsSkeletonLoader } from '@node-space/ui-components'
import { Lottie } from 'components/Animations/SimpleLottie'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useCustomerQuery } from 'hooks/queries/Customers/useCustomerQuery'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { NestedCustomerDetails } from 'types/customers'
import { isBusinessCustomer } from '../utils'
import { BusinessCustomerSidepanelView } from './BusinessCustomerSidepanelView'
import { IndividualCustomerSidePanelView } from './IndividualCustomerSidePanelView'

type SidePanelCustomerBodyProps = {
  customerId: string
}

export const SidePanelCustomerBody = ({ customerId }: SidePanelCustomerBodyProps) => {
  const { t } = useTranslation()
  const { data: customer, error, isPending } = useCustomerQuery<NestedCustomerDetails>(customerId)

  const {
    data: countries,
    isPending: isCountriesLoading,
    isError: isCountriesLoadingError,
  } = useCountriesQuery(true)

  if (isPending) {
    return <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
  }

  if (error) {
    return (
      <EmptyState
        testId="error-state"
        LottiePlayer={<Lottie src="connection-error" />}
        title={t('requestErrors.pageErrorIntro')}
        description={
          <>
            {`${t('requestErrors.comeBackLater')}`}{' '}
            <Button variant="ghost" noPadding onClick={() => window.location.reload()}>
              {t('requestErrors.refreshPage')}
            </Button>
          </>
        }
      />
    )
  }

  return (
    <Box flexCol gapY={4}>
      <h4 className="text-h4 text-foreground-prominent">{t('customers.customerDetails')}</h4>
      {isBusinessCustomer(customer) ? (
        <BusinessCustomerSidepanelView
          customer={customer}
          countries={countries}
          isCountriesLoading={isCountriesLoading}
          isCountriesLoadingError={isCountriesLoadingError}
        />
      ) : (
        <IndividualCustomerSidePanelView
          customer={customer}
          countries={countries}
          isCountriesLoading={isCountriesLoading}
          isCountriesLoadingError={isCountriesLoadingError}
        />
      )}
    </Box>
  )
}
